import { Box, Typography } from '@mui/material';
import { CARD_TEXT, ProfileCard } from './ProfileCard';
import { StatusChip, type RosterChipKind } from '../StatusChip';
import { exitDate, monitoringWindow, profileYear } from '../profileText';
import type { StudentProfile } from '../studentProfileApi';

function exitedLine(profile: StudentProfile): string | null {
  const { exitYearEnd, exitMonth, exitReason } = profile.elStatus;
  const parts = [exitYearEnd ? exitDate(exitYearEnd, exitMonth ?? 0) : '', exitReason ? exitReason.toLowerCase() : ''].filter(Boolean);
  return parts.length ? `Exited: ${parts.join(' · ')}` : null;
}

function monitoringLine(profile: StudentProfile): string | null {
  const { exitYearEnd } = profile.elStatus;
  if (!exitYearEnd) return null;
  const years = Math.max(1, Number(profile.currentSchoolYear.slice(0, 4)) + 1 - exitYearEnd);
  if (years > profile.monitoringYears) return `Monitoring: ${years} years after exit; ${monitoringWindow(profile.monitoringYears, true)}`;
  const source = profile.monitoringYearsKnown ? '' : ' (the usual window; the district’s setting could not be read)';
  return `Monitoring: year ${years} of ${profile.monitoringYears}${source}`;
}

function standingLines(profile: StudentProfile): (string | null)[] {
  const { activeSinceYearEnd, yearsInProgram } = profile.elStatus;
  const years = yearsInProgram != null ? `Years in program: ${yearsInProgram}` : null;
  switch (profile.standing) {
    case 'active':
      return [activeSinceYearEnd ? `Active since: ${profileYear(activeSinceYearEnd)}` : null, years];
    case 'monitoring':
      return [exitedLine(profile), years, monitoringLine(profile)];
    case 'former':
      return [exitedLine(profile), years];
    case 'waived':
      return [years];
    case 'departed':
      return [
        profile.lastSeenSchoolYearEnd ? `Last enrolled: ${profileYear(profile.lastSeenSchoolYearEnd)}` : null,
        exitedLine(profile) ?? 'Status then: Active EL',
      ];
    default:
      return [];
  }
}

const SIS_FACTS: { label: string; value: (profile: StudentProfile) => string | undefined }[] = [
  { label: 'Program', value: (profile) => profile.student.programModel },
  { label: 'Home language', value: (profile) => profile.student.homeLanguage },
];

function linesOf(profile: StudentProfile): (string | null)[] {
  return [...standingLines(profile), ...SIS_FACTS.map(({ label, value }) => (value(profile)?.trim() ? `${label}: ${value(profile)!.trim()}` : null))];
}

export function ElStatusCard({ profile }: { profile: StudentProfile }) {
  const kinds: RosterChipKind[] = [...(profile.standing === 'unknown' ? [] : [profile.standing]), ...(profile.cohorts as RosterChipKind[])];
  return (
    <ProfileCard title="EL status">
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <Typography sx={{ ...CARD_TEXT.lead, flexShrink: 0 }}>Status:</Typography>
        {kinds.length ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {kinds.map((kind) => <StatusChip key={kind} kind={kind} />)}
          </Box>
        ) : (
          <Typography sx={CARD_TEXT.lead}>Not in your file</Typography>
        )}
      </Box>
      {linesOf(profile).filter((line): line is string => line != null).map((line) => (
        <Typography key={line} data-testid="el-status-line" sx={CARD_TEXT.lead}>{line}</Typography>
      ))}
    </ProfileCard>
  );
}
